import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search, Users, Building2, FileText, Ticket,
  X, Loader2, ArrowRight
} from 'lucide-react';

const typeConfig = {
  agent: { icon: Users, color: 'bg-sky-100 text-sky-700', label: 'Agent' },
  agency: { icon: Building2, color: 'bg-emerald-100 text-emerald-700', label: 'Agency' },
  contract: { icon: FileText, color: 'bg-amber-100 text-amber-700', label: 'Contract' },
  ticket: { icon: Ticket, color: 'bg-rose-100 text-rose-700', label: 'Ticket' },
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
    if (!open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await searchAPI.search(q);
      setResults(res.data.results || []);
      setSelectedIndex(-1);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (result) => {
    setOpen(false);
    navigate(result.url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-300 hover:bg-white/5 transition-colors"
        data-testid="global-search-trigger"
      >
        <Search size={16} />
        <span className="hidden md:inline">Search...</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono text-slate-500">
          <span>⌘</span>K
        </kbd>
      </button>

      {/* Search overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" data-testid="global-search-overlay">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            ref={containerRef}
            className="relative w-full max-w-xl mx-4 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="global-search-modal"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
              <Search size={18} className="text-slate-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search agents, agencies, contracts, tickets..."
                value={query}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400 text-slate-900"
                data-testid="global-search-input"
              />
              {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100">
                <X size={16} className="text-slate-400" />
              </button>
            </div>

            {/* Results */}
            <ScrollArea className="max-h-80">
              {query.length < 2 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  Type at least 2 characters to search
                </div>
              ) : results.length === 0 && !loading ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  No results found for "{query}"
                </div>
              ) : (
                <div className="py-2">
                  {results.map((result, i) => {
                    const config = typeConfig[result.type] || typeConfig.agent;
                    const Icon = config.icon;
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSelect(result)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
                          i === selectedIndex ? 'bg-slate-50' : ''
                        }`}
                        data-testid={`search-result-${result.type}-${i}`}
                      >
                        <div className={`p-1.5 rounded-md ${config.color}`}>
                          <Icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{result.title}</p>
                          <p className="text-xs text-slate-500 truncate">{result.subtitle}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">{config.label}</Badge>
                        <ArrowRight size={14} className="text-slate-300 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-[10px] text-slate-400">
              <span>Navigate with ↑↓ · Enter to select · Esc to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
